/**
 * Hooks pour le catalogue produits et catégories (API backend).
 */
import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_PREFIX = `${API}/api`;

/**
 * Récupère les catégories (liste publique).
 * @param {Object} opts - mainOnly: si true, ne retourne que les catégories principales (parentId null)
 */
export function useCategories(opts = {}) {
  const { mainOnly = false } = opts;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const url = mainOnly ? `${API_PREFIX}/categories?main_only=true` : `${API_PREFIX}/categories`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((data) => { if (!cancelled) setCategories(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setError(e.message || "Erreur"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mainOnly]);
  return { categories, loading, error };
}

/**
 * Récupère les produits avec filtres et pagination.
 * @param {Object} params - category, brand, search, min_price, max_price, in_stock, featured, sort, page, limit
 */
export function useProducts(params = {}) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 24 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) qs.set(k, v);
    });
    let cancelled = false;
    setLoading(true);
    fetch(`${API_PREFIX}/products?${qs.toString()}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => { if (!cancelled) setData({ items: d.items || [], total: d.total || 0, page: d.page || 1, limit: d.limit || 24 }); })
      .catch((e) => { if (!cancelled) setError(e.message || "Erreur"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.category, params.brand, params.search, params.min_price, params.max_price, params.in_stock, params.featured, params.sort, params.page, params.limit, params.store_id]);
  return { ...data, loading, error };
}

/**
 * Récupère une catégorie par slug.
 */
export function useCategoryBySlug(slug) {
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) { setCategory(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_PREFIX}/categories/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (res.status === 404) return null;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => { if (!cancelled) setCategory(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);
  return { category, loading, error };
}

/**
 * Catégorie avec breadcrumb (pour page catégorie et fil d'Ariane).
 */
export function useCategoryWithBreadcrumb(slug) {
  const [data, setData] = useState({ category: null, breadcrumb: [] });
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) { setData({ category: null, breadcrumb: [] }); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_PREFIX}/categories/${encodeURIComponent(slug)}/with-breadcrumb`)
      .then(async (res) => {
        if (res.status === 404) return null;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        if (!cancelled && d) setData({ category: d, breadcrumb: d.breadcrumb || [] });
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);
  return { ...data, loading, error };
}

/**
 * Enfants directs d'une catégorie (sous-catégories).
 */
export function useCategoryChildren(slug) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) { setChildren([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_PREFIX}/categories/${encodeURIComponent(slug)}/children`)
      .then(async (res) => {
        if (res.status === 404) return [];
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => { if (!cancelled) setChildren(Array.isArray(d) ? d : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);
  return { children, loading, error };
}

/**
 * Arborescence complète des catégories (pour menu ou sidebar).
 */
export function useCategoryTree() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_PREFIX}/categories/tree`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => { if (!cancelled) setTree(Array.isArray(d) ? d : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { tree, loading, error };
}

/**
 * Récupère un produit par slug.
 */
export function useProductBySlug(slug) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) { setProduct(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_PREFIX}/products/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (res.status === 404) return null;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Erreur ${res.status}`);
        }
        return res.json();
      })
      .then((d) => { if (!cancelled) setProduct(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);
  return { product, loading, error };
}

